import { useState } from 'react'

import { Segmented } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { Pager } from '@/components/pos/office/Pager'
import { useSession } from '@/contexts/SessionContext'
import { useBranchTerminals, useOfficeQuery, useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'
import { formatDateTime, formatMoney } from '@/utils'

type Tab = 'shifts' | 'cash'

/** Every shift and every cash move of this branch's terminals, from the server (B16). */
export const ShiftsHistoryPage = () => {
  const { can, device } = useSession()
  const online = useOnline()
  const tabs = ([['shifts', 'pos_reports:shifts', 'Shifts'], ['cash', 'pos:cash_in', 'Cash in / out']] as const).filter(([, key]) => can(key))
  const [tab, setTab] = useState<Tab>(tabs[0]?.[0] ?? 'shifts')
  const [page, setPage] = useState(1)
  const { data: terminals = [] } = useBranchTerminals()
  const terminalIds = terminals.map((t) => String(t._id)).join(',')
  const terminalName = (id: unknown) => {
    const t = terminals.find((x) => String(x._id) === String(id))
    return t ? `T${t.unique_no} · ${t.name}` : ''
  }

  const shifts = useOfficeQuery(['shift-reports', page], () => OfficeModel.shiftReports({ branch_id: device!.branch_id, page }), tab === 'shifts')
  const cash = useOfficeQuery(['cash', terminalIds, page], () => OfficeModel.cashMoves({ terminal_id: terminalIds, page }), tab === 'cash' && !!terminalIds)
  const current = tab === 'shifts' ? shifts : cash

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Shift history" subtitle="All terminals of this branch.">
        {tabs.length > 1 && (
          <Segmented<Tab> value={tab} onChange={(t) => (setTab(t), setPage(1))} options={tabs.map(([value, , label]) => ({ value, label }))} />
        )}
      </PageHeader>
      {!online ? (
        <NeedsConnection />
      ) : (
        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
          {tab === 'shifts' ? (
            <OfficeTable
              loading={shifts.isFetching}
              rows={shifts.data?.result ?? []}
              rowKey={(r) => String(r._id)}
              columns={[
                { label: 'Terminal', cell: (r) => <span className="font-medium text-ink">{terminalName(r.terminal_id)}</span> },
                { label: 'Opened', cell: (r) => formatDateTime(r.opened_at) },
                { label: 'Closed', cell: (r) => (r.closed_at ? formatDateTime(r.closed_at) : 'Open') },
                { label: 'Orders', className: 'text-right', cell: (r) => r.order_count ?? 0 },
                { label: 'Cash', className: 'text-right tabular-nums', cell: (r) => formatMoney(Number(r.sales?.cash ?? 0)) },
                {
                  label: 'Other',
                  className: 'hidden text-right tabular-nums lg:table-cell',
                  cell: (r) => formatMoney(['card', 'bank', 'qr', 'credit'].reduce((s, k) => s + Number(r.sales?.[k] ?? 0), 0)),
                },
                { label: 'Refunds', className: 'hidden text-right tabular-nums md:table-cell', cell: (r) => formatMoney(Number(r.refunds ?? 0)) },
                { label: 'Expected', className: 'text-right tabular-nums', cell: (r) => formatMoney(Number(r.expected_cash ?? 0)) },
                {
                  label: 'Difference',
                  className: 'text-right tabular-nums',
                  cell: (r) => (r.discrepancy == null ? '—' : <span className={Number(r.discrepancy) < 0 ? 'text-error' : 'text-ink'}>{formatMoney(Number(r.discrepancy))}</span>),
                },
              ]}
            />
          ) : (
            <OfficeTable
              loading={cash.isFetching}
              rows={cash.data?.result ?? []}
              rowKey={(r) => String(r._id)}
              columns={[
                { label: 'When', cell: (r) => formatDateTime(r.created_at) },
                { label: 'Terminal', cell: (r) => terminalName(r.terminal_id) },
                { label: 'Type', cell: (r) => (r.type === 'cash_in' ? 'Cash in' : 'Cash out') },
                { label: 'Amount', className: 'text-right tabular-nums', cell: (r) => formatMoney(Number(r.amount ?? 0)) },
                { label: 'Reason', className: 'hidden md:table-cell', cell: (r) => r.remark ?? '' },
              ]}
            />
          )}
          <Pager page={page} count={current.data?.count ?? 0} onChange={setPage} />
        </div>
      )}
    </div>
  )
}
